import React, { useEffect, useState } from 'react';
import { HelpCircle, AlertTriangle } from 'lucide-react';
import { ValuationData, ValidationErrors, Assets } from '../../../types/valuation';
import { Tooltip } from '../../ui/Tooltip';
import clsx from 'clsx';

interface PropertyDetailsProps {
  data: ValuationData;
  onUpdate: (data: Partial<ValuationData>) => void;
  errors?: ValidationErrors;
}

const propertyConditions = ["New", "Good", "Average", "Fair"];
const ustAgeOptions = ["0-5 Years", "6-10 Years", "11-15 Years", "15+ Years"];

const propertyTypes = [
  {
    id: 'convenience-store',
    label: 'Convenience Store',
    image: 'https://app.thefuelcentral.com/storage/v1/object/public/Stock%20Photos/c-store.jpg',
    description: 'Modern c-store layout with built-in cabinetry, FF&E, hot/cold food prep and storage, generally 2,000 sq ft more in size'
  },
  {
    id: 'service-station',
    label: 'Service Station',
    image: 'https://app.thefuelcentral.com/storage/v1/object/public/Stock%20Photos/service-station.jpg',
    description: 'Legacy layout, typically attached to service bays, minimal FF&E and permanently attached equipment'
  },
  {
    id: 'kiosk',
    label: 'Kiosk',
    image: 'https://app.thefuelcentral.com/storage/v1/object/public/Stock%20Photos/kiosk.jpg',
    description: 'Smaller manned payment points, very minimal storage and no customer walk-in, typically 50-250 sq ft in size'
  },
  {
    id: 'food-booth',
    label: 'Food Booth',
    image: 'https://app.thefuelcentral.com/storage/v1/object/public/Stock%20Photos/food-booth.jpg',
    description: 'Older convenience store style building, usually limited built-in cooler units and limited shelving and FF&E, typically 500-1,500 sq ft in size'
  }
];

export default function PropertyDetails({ data, onUpdate, errors }: PropertyDetailsProps) {
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [yearError, setYearError] = useState<string | null>(null);
  const [lotSizeError, setLotSizeError] = useState<string | null>(null);
  const [shouldShakeLotSize, setShouldShakeLotSize] = useState(false);
  const [showCarWash, setShowCarWash] = useState<boolean>(false);

  console.log('PropertyDetails render - lotSizeError:', lotSizeError, 'shouldShakeLotSize:', shouldShakeLotSize);

  // Initialize showCarWash based on existing data OR database suggestions
  useEffect(() => {
    const hasCarWashData = (data.carWashSize && data.carWashSize > 0) ||
                          (data.carWashBuildingSize && data.carWashBuildingSize > 0) ||
                          (data.carWashLength && data.carWashLength > 0) ||
                          data.assets?.Car_Wash?.condition ||
                          data.assets?.Car_Wash_Equipment?.condition;

    // Also check if database has suggested car wash size or length
    const hasDatabaseCarWashSuggestion = (data.databaseSuggestions?.carWashSize && data.databaseSuggestions.carWashSize > 0) ||
                                         (data.databaseSuggestions?.carWashLength && data.databaseSuggestions.carWashLength > 0);

    // Show car wash section if either user data exists OR database has suggestions
    const shouldShowCarWash = Boolean(hasCarWashData || hasDatabaseCarWashSuggestion);
    setShowCarWash(shouldShowCarWash);

    // Auto-check the hasCarWash checkbox if database has suggestions
    if (hasDatabaseCarWashSuggestion && !data.hasCarWash) {
      onUpdate({ hasCarWash: true });
    }
  }, [data.databaseSuggestions?.carWashSize, data.databaseSuggestions?.carWashLength]);

  // Store original database values for suggestions
  const [databaseValues, setDatabaseValues] = useState<{
    propertySize?: number;
    lotSize?: number;
    yearBuilt?: number;
    canopySize?: number;
  }>({});

  // Capture database values when station is first loaded
  useEffect(() => {
    if (data.stationObjectId && data.location &&
        (data.propertySize !== undefined || data.lotSize !== undefined || data.yearBuilt !== undefined)) {
      // Store the database values for suggestions
      setDatabaseValues({
        propertySize: data.propertySize,
        lotSize: data.lotSize,
        yearBuilt: data.yearBuilt,
        canopySize: data.canopySize
      });
    }
  }, [data.stationObjectId, data.location]);

  // Component to show database suggestion
  const DatabaseSuggestion = ({ fieldName, databaseValue, onAccept }: {
    fieldName: string;
    databaseValue: number;
    onAccept: () => void;
  }) => (
    <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-blue-700">
            💡 Our records show: <strong>{databaseValue.toLocaleString()}</strong>
          </span>
        </div>
        <button
          type="button"
          onClick={onAccept}
          className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
        >
          Use This Value
        </button>
      </div>
      <div className="text-xs text-gray-500 mt-1.5 leading-snug">
        Values are sourced from automated measurements, public records, or previously entered data and may not be exact.
        Some measurements are based on drip line rather than actual building footprint. Use as a starting point only — we are not responsible for inaccuracies.
      </div>
    </div>
  );

  // Trigger lot size validation when wizard validation fails
  useEffect(() => {
    console.log('useEffect triggered - validationErrors:', data.validationErrors?.lotSize, 'lotSize:', data.lotSize);
    if (data.validationErrors?.lotSize) {
      console.log('Triggering lot size validation from useEffect');
      validateLotSize(data.lotSize);
    }
  }, [data.validationErrors?.lotSize, data.lotSize]);

  const validateSizes = (buildingSize: number, lotSize: number) => {
    if (buildingSize > lotSize && lotSize !== 0) {
      setSizeError('Building size cannot exceed lot size. Please verify your measurements.');
    } else {
      setSizeError(null);
    }
  };

  const validateLotSize = (lotSize: number | undefined) => {
    console.log('validateLotSize called with:', lotSize);
    if (!lotSize || lotSize === 0) {
      console.log('Lot size validation failed, setting error and shake');
      setLotSizeError('Lot size is required to proceed');
      setShouldShakeLotSize(true);
      // Remove shake animation after animation completes
      setTimeout(() => setShouldShakeLotSize(false), 600);
      return false;
    } else {
      console.log('Lot size validation passed, clearing error');
      setLotSizeError(null);
      setShouldShakeLotSize(false);
      return true;
    }
  };

  const validateYears = (yearBuilt: number | undefined, yearRenovated: number | undefined) => {
    if (yearBuilt && yearRenovated && yearBuilt > yearRenovated) {
      setYearError('Year Built cannot be greater than Year Renovated. Please check your dates.');
      return false;
    } else {
      setYearError(null);
      return true;
    }
  };

  useEffect(() => {
    validateSizes(data.propertySize || 0, data.lotSize || 0);
  }, [data.propertySize, data.lotSize]);

  useEffect(() => {
    validateYears(data.yearBuilt, data.yearRenovated);
  }, [data.yearBuilt, data.yearRenovated]);

  useEffect(() => {
    if (data.propertySize !== undefined || data.propertyCondition) {
      const currentAssets = data.assets || {};

      // Determine which building asset to update based on property type
      const buildingAssetKey = data.propertyType === 'convenience-store' ? 'Convenience_Store' :
                              data.propertyType === 'service-station' ? 'Service_Station' :
                              data.propertyType === 'kiosk' ? 'Kiosk' :
                              data.propertyType === 'food-booth' ? 'Food_Booth' : 'Convenience_Store';

      const updatedAssets = {
        ...currentAssets,
        FFE: {
          condition: data.propertyCondition || 'Average',
          size: data.propertySize || 0
        }
      };

      // Update the appropriate building asset
      if (data.propertyType && data.propertySize !== undefined) {
        if (buildingAssetKey === 'Convenience_Store') {
          updatedAssets.Convenience_Store = {
            condition: data.propertyCondition || currentAssets.Convenience_Store?.condition || 'Average',
            size: data.propertySize || 0
          };
        } else if (buildingAssetKey === 'Service_Station') {
          updatedAssets.Service_Station = {
            condition: data.propertyCondition || currentAssets.Service_Station?.condition || 'Average',
            size: data.propertySize || 0
          };
        } else if (buildingAssetKey === 'Kiosk') {
          updatedAssets.Kiosk = {
            condition: data.propertyCondition || currentAssets.Kiosk?.condition || 'Average',
            size: data.propertySize || 0
          };
        } else if (buildingAssetKey === 'Food_Booth') {
          updatedAssets.Food_Booth = {
            condition: data.propertyCondition || currentAssets.Food_Booth?.condition || 'Average',
            size: data.propertySize || 0
          };
        }
      }

      onUpdate({ assets: updatedAssets });
    }
  }, [data.propertySize, data.propertyCondition, data.propertyType]);

  const formatNumber = (value: number | undefined): string => {
    if (!value) return '';
    return value.toLocaleString();
  };
  
  // Format year as whole number without commas
  const formatYear = (value: number | undefined): string => {
    if (!value) return '';
    return value.toString();
  };

  const parseNumber = (value: string): number => {
    const numericValue = parseInt(value.replace(/,/g, ''), 10);
    return isNaN(numericValue) ? 0 : numericValue;
  };

  const parseTankCapacity = (value: string): number => {
    const numericValue = parseInt(value.replace(/,/g, ''), 10);
    return isNaN(numericValue) ? 0 : numericValue; // Return 0 instead of defaulting to 20,000
  };

  // Set default UST age if not selected
  useEffect(() => {
    if (!data.ustAge) {
      const defaultAge = '11-15 Years';
      const defaultCondition = 'Average';

      onUpdate({
        ustAge: defaultAge,
        ustCondition: defaultCondition,
        assets: {
          ...data.assets,
          UST: {
            condition: defaultCondition,
            size: data.tankCapacity || 0 // Don't default to 20000, use actual value or 0
          }
        }
      });
    }
  }, []); // Run only once on component mount

  // Update UST asset when tank capacity changes
  useEffect(() => {
    if (data.tankCapacity !== undefined) {
      onUpdate({
        assets: {
          ...data.assets,
          UST: {
            condition: data.ustCondition || data.assets?.UST?.condition || 'Average',
            size: data.tankCapacity
          }
        }
      });
    }
  }, [data.tankCapacity, data.ustCondition]); // Update when either tank capacity or UST condition changes

  // Update Site_Improvements size when lot size changes
  useEffect(() => {
    if (data.lotSize !== undefined && data.assets?.Site_Improvements) {
      onUpdate({
        assets: {
          ...data.assets,
          Site_Improvements: {
            ...data.assets.Site_Improvements,
            size: data.lotSize
          }
        }
      });
    }
  }, [data.lotSize]); // Update Site_Improvements size when lot size changes

  return (
    <div className="space-y-6">
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-700">
          Describe the physical property — building type, sizes, conditions, and infrastructure. These details directly impact the cost approach component of your valuation. If we have records on file for your property, we'll suggest values you can accept or override.
        </p>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Please Select Your Building Type
          </label>
          <Tooltip content="Select the type of property">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="grid grid-cols-2 gap-6">
          {propertyTypes.map((type) => (
            <div key={type.id} className="flex flex-col items-center text-center">
              <div 
                className={clsx(
                  "relative w-full aspect-video rounded-lg overflow-hidden cursor-pointer border-2 mb-3",
                  data.propertyType === type.id 
                    ? 'border-fc-orange-500 ring-4 ring-fc-orange-300 shadow-lg'
                    : 'border-gray-200 hover:border-fc-orange-300'
                )}
                onClick={() => {
                  // Get the corresponding asset key based on property type
                  const assetKey = type.id === 'convenience-store' ? 'Convenience_Store' :
                                   type.id === 'service-station' ? 'Service_Station' :
                                   type.id === 'kiosk' ? 'Kiosk' :
                                   type.id === 'food-booth' ? 'Food_Booth' : null;

                  if (assetKey) {
                    // Start with only the universal assets that apply to all property types
                    const updatedAssets: Assets = {
                      // Universal assets that apply to all gas stations
                      Fueling_Canopy: data.assets?.Fueling_Canopy || { condition: 'Average', size: 0 },
                      UST: data.assets?.UST || { condition: 'Average', size: 0 },
                      Fueling_Equipment: data.assets?.Fueling_Equipment || { condition: 'Average', size: 0 },
                      Site_Improvements: data.assets?.Site_Improvements || { condition: 'Average', size: 0 },
                      FFE: data.assets?.FFE || { condition: 'Average', size: 0 }
                    };

                    // Add the selected property type asset
                    if (assetKey === 'Convenience_Store') {
                      updatedAssets.Convenience_Store = {
                        condition: data.propertyCondition || 'Average',
                        size: data.propertySize || 0
                      };
                    } else if (assetKey === 'Service_Station') {
                      updatedAssets.Service_Station = {
                        condition: data.propertyCondition || 'Average',
                        size: data.propertySize || 0
                      };
                    } else if (assetKey === 'Kiosk') {
                      updatedAssets.Kiosk = {
                        condition: data.propertyCondition || 'Average',
                        size: data.propertySize || 0
                      };
                    } else if (assetKey === 'Food_Booth') {
                      updatedAssets.Food_Booth = {
                        condition: data.propertyCondition || 'Average',
                        size: data.propertySize || 0
                      };
                    }

                    // Preserve car wash assets if they exist
                    if (data.assets?.Car_Wash || data.assets?.Car_Wash_Equipment) {
                      if (data.assets.Car_Wash) updatedAssets.Car_Wash = data.assets.Car_Wash;
                      if (data.assets.Car_Wash_Equipment) updatedAssets.Car_Wash_Equipment = data.assets.Car_Wash_Equipment;
                    }

                    onUpdate({
                      propertyType: type.id,
                      assets: updatedAssets
                    });
                  } else {
                    onUpdate({ propertyType: type.id });
                  }
                }}
              >
                <img 
                  src={type.image} 
                  alt={type.label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
                  <span className="text-white font-semibold text-lg">{type.label}</span>
                </div>
                {data.propertyType === type.id && (
                  <div className="absolute top-2 right-2 bg-fc-orange-500 text-white rounded-full p-1 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-700 mb-2 h-20 overflow-auto px-1">{type.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="grid grid-cols-2 gap-6">
          {/* Building Size */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Building Size (sq ft)
              </label>
              <Tooltip content="Total building size including store and service areas">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            {/* Always show database suggestion if available */}
            {data.databaseSuggestions?.propertySize && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-700">
                      💡 Our records show: <strong>{data.databaseSuggestions.propertySize.toLocaleString()} sq ft</strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate({ propertySize: data.databaseSuggestions?.propertySize })}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Use This Value
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from automated measurements, public records, or prior entries. Some measurements may be based on drip line. Use as a starting point only.</p>
              </div>
            )}
            <input
              type="text"
              value={data.propertySize ? formatNumber(data.propertySize) : ''}
              onChange={(e) => {
                const value = parseNumber(e.target.value);
                onUpdate({ propertySize: value });
                validateSizes(value, data.lotSize || 0);
              }}
              className={clsx(
                "w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                sizeError ? "border-red-500" : "border-gray-300"
              )}
              placeholder="e.g., 2,500"
            />
          </div>

          {/* Building Condition */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Condition of Building Improvements
              </label>
              <Tooltip content="Assess the overall condition of the building's structure, exterior, and interior finishes. Consider factors such as the age of the building, presence of any visible damage or deterioration, and the need for any major repairs or renovations. Examples:&#13;&#10;New: Like new, recently constructed or renovated with high-quality materials.&#13;&#10;Good: Well-maintained with minor wear and tear, no major repairs needed.&#13;&#10;Average: Showing signs of age and wear, may require some minor repairs or cosmetic updates.&#13;&#10;Fair: Significant deterioration, visible damage, in need of major repairs or renovations.">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <div className="flex gap-2">
              {propertyConditions.map((condition) => (
                <button
                  key={condition}
                  onClick={() => {
                    // Determine which asset type to update based on property type
                    const assetToUpdate = data.propertyType === 'convenience-store' ? 'Convenience_Store' :
                                         data.propertyType === 'service-station' ? 'Service_Station' :
                                         data.propertyType === 'kiosk' ? 'Kiosk' :
                                         data.propertyType === 'food-booth' ? 'Food_Booth' : 'Convenience_Store';

                    // Create updated assets object preserving existing structure
                    const updatedAssets = { ...data.assets };

                    // Update the building type asset condition
                    if (assetToUpdate === 'Convenience_Store') {
                      updatedAssets.Convenience_Store = {
                        condition,
                        size: data.propertySize || 0
                      };
                    } else if (assetToUpdate === 'Service_Station') {
                      updatedAssets.Service_Station = {
                        condition,
                        size: data.propertySize || 0
                      };
                    } else if (assetToUpdate === 'Kiosk') {
                      updatedAssets.Kiosk = {
                        condition,
                        size: data.propertySize || 0
                      };
                    } else if (assetToUpdate === 'Food_Booth') {
                      updatedAssets.Food_Booth = {
                        condition,
                        size: data.propertySize || 0
                      };
                    }

                    onUpdate({
                      propertyCondition: condition,
                      assets: updatedAssets
                    });
                  }}
                  className={clsx(
                    "px-4 py-2 rounded text-sm font-medium flex-1",
                    ((data.propertyType === 'convenience-store' && data.assets?.Convenience_Store?.condition === condition) ||
                    (data.propertyType === 'service-station' && data.assets?.Service_Station?.condition === condition) ||
                    (data.propertyType === 'kiosk' && data.assets?.Kiosk?.condition === condition) ||
                    (data.propertyType === 'food-booth' && data.assets?.Food_Booth?.condition === condition))
                      ? "bg-fc-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {condition}
                </button>
              ))}
            </div>
          </div>

          {/* Canopy Size */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Canopy Size (sq ft)
              </label>
              <Tooltip content="Total size of fuel canopy area">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            {/* Always show database suggestion if available */}
            {data.databaseSuggestions?.canopySize && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-700">
                      💡 Our records show: <strong>{data.databaseSuggestions.canopySize.toLocaleString()} sq ft</strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const suggestedCanopySize = data.databaseSuggestions?.canopySize || 0;
                      onUpdate({
                        canopySize: suggestedCanopySize,
                        assets: {
                          ...data.assets,
                          Fueling_Canopy: {
                            condition: data.assets?.Fueling_Canopy?.condition || 'Average',
                            size: suggestedCanopySize
                          }
                        }
                      });
                    }}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Use This Value
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from automated measurements, public records, or prior entries. Some measurements may be based on drip line. Use as a starting point only.</p>
              </div>
            )}
            <input
              type="text"
              value={data.canopySize ? formatNumber(data.canopySize) : ''}
              onChange={(e) => {
                const newCanopySize = parseNumber(e.target.value);
                onUpdate({
                  canopySize: newCanopySize,
                  assets: {
                    ...data.assets,
                    Fueling_Canopy: {
                      condition: data.assets?.Fueling_Canopy?.condition || 'Average',
                      size: newCanopySize
                    }
                  }
                });
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 3,000"
            />
          </div>

          {/* Canopy Condition */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Condition of Fueling Canopy
              </label>
              <Tooltip content="Evaluate the condition of the canopy structure, covering, and supporting columns. Look for signs of rust, damage, or leaks. Consider the age and overall aesthetic appeal of the canopy. Examples:&#13;&#10;New: New or recently replaced canopy in excellent condition.&#13;&#10;Good: Well-maintained canopy with minimal wear and tear.&#13;&#10;Average: Canopy showing signs of age, may have minor damage or require some repairs.&#13;&#10;Fair: Canopy with significant damage, rust, or leaks, detracting from the property's appearance and functionality.">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <div className="flex gap-2">
              {propertyConditions.map((condition) => (
                <button
                  key={condition}
                  onClick={() => {
                    onUpdate({
                      assets: {
                        ...data.assets,
                        Fueling_Canopy: {
                          condition,
                          size: data.canopySize || 0
                        }
                      }
                    });
                  }}
                  className={clsx(
                    "px-4 py-2 rounded text-sm font-medium flex-1",
                    data.assets?.Fueling_Canopy?.condition === condition
                      ? "bg-fc-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {condition}
                </button>
              ))}
            </div>
          </div>
          
          {/* Car Wash Checkbox */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="hasCarWash"
                checked={showCarWash}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowCarWash(checked);
                  
                  // If unchecking, clear all car wash data
                  if (!checked) {
                    onUpdate({
                      carWashSize: undefined,
                      carWashBuildingSize: undefined,
                      carWashLength: undefined,
                      assets: {
                        ...data.assets,
                        Car_Wash: undefined,
                        Car_Wash_Equipment: undefined
                      }
                    });
                  }
                }}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="hasCarWash" className="text-sm font-medium text-gray-700 cursor-pointer">
                Is there a car wash?
              </label>
              <Tooltip content="Check this box if the property has a car wash facility to enter size and condition details">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
          </div>
          
          {/* Car Wash Size and Condition - Only show if checkbox is checked */}
          {showCarWash && (
            <>
              {/* Car Wash Size (left) and Condition (right) */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Car Wash Size (sq ft)
                  </label>
                  <Tooltip content="Total size of the car wash area in square feet">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                {/* Database suggestion for car wash size */}
                {data.databaseSuggestions?.carWashSize && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-blue-700">
                          💡 Our records show: <strong>{data.databaseSuggestions.carWashSize.toLocaleString()} sq ft</strong>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const suggestedSize = data.databaseSuggestions?.carWashSize || 0;
                          onUpdate({
                            carWashSize: suggestedSize,
                            assets: {
                              ...data.assets,
                              Car_Wash: {
                                condition: data.assets?.Car_Wash?.condition || 'Average',
                                size: suggestedSize
                              }
                            }
                          });
                        }}
                        className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Use This Value
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from automated measurements, public records, or prior entries. Use as a starting point only.</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formatNumber(data.carWashSize || data.carWashBuildingSize)}
                  onChange={(e) => {
                    const value = parseNumber(e.target.value);
                    onUpdate({
                      carWashSize: value,
                      assets: {
                        ...data.assets,
                        Car_Wash: {
                          condition: data.assets?.Car_Wash?.condition || 'Average',
                          size: value
                        }
                      }
                    });
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1,200"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Condition of Car Wash
                  </label>
                  <Tooltip content="Assess the overall condition of the car wash equipment, structure, and functionality. Consider factors such as age, maintenance, and operational status.">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <div className="flex gap-2">
                  {propertyConditions.map((condition) => (
                    <button
                      key={condition}
                      onClick={() => {
                        onUpdate({
                          assets: {
                            ...data.assets,
                            Car_Wash: {
                              condition,
                              size: data.carWashSize || data.carWashBuildingSize || 0
                            },
                            Car_Wash_Equipment: {
                              condition,
                              size: data.carWashLength || 0
                            }
                          }
                        });
                      }}
                      className={clsx(
                        "px-4 py-2 rounded text-sm font-medium flex-1",
                        data.assets?.Car_Wash?.condition === condition
                          ? "bg-fc-orange-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      {condition}
                    </button>
                  ))}
                </div>
              </div>

              {/* Car Wash Length */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Car Wash Length (ft)
                  </label>
                  <Tooltip content="Length of the car wash tunnel or bay">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                {/* Database suggestion for car wash length */}
                {data.databaseSuggestions?.carWashLength && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-blue-700">
                          💡 Our records show: <strong>{data.databaseSuggestions.carWashLength.toLocaleString()} ft</strong>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const suggestedLength = data.databaseSuggestions?.carWashLength || 0;
                          onUpdate({
                            carWashLength: suggestedLength,
                            assets: {
                              ...data.assets,
                              Car_Wash_Equipment: {
                                condition: data.assets?.Car_Wash?.condition || 'Average',
                                size: suggestedLength
                              }
                            }
                          });
                        }}
                        className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Use This Value
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from automated measurements, public records, or prior entries. Use as a starting point only.</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formatNumber(data.carWashLength)}
                  onChange={(e) => {
                    const value = parseNumber(e.target.value);
                    onUpdate({
                      carWashLength: value,
                      assets: {
                        ...data.assets,
                        Car_Wash_Equipment: {
                          condition: data.assets?.Car_Wash?.condition || 'Average',
                          size: value
                        }
                      }
                    });
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 40"
                />
              </div>

              {/* Empty div to push Lot Size to next row */}
              <div></div>
            </>
          )}

          {/* Lot Size */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Lot Size (sq ft)
              </label>
              <Tooltip content="Total lot size including all paved areas and landscaping">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            {/* Always show database suggestion if available */}
            {data.databaseSuggestions?.lotSize && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-700">
                      💡 Our records show: <strong>{data.databaseSuggestions.lotSize.toLocaleString()} sq ft</strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate({ lotSize: data.databaseSuggestions?.lotSize })}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Use This Value
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from automated measurements, public records, or prior entries. Use as a starting point only.</p>
              </div>
            )}
            <input
              type="text"
              value={data.lotSize ? formatNumber(data.lotSize) : ''}
              onChange={(e) => {
                const value = parseNumber(e.target.value);
                onUpdate({ lotSize: value });
                validateSizes(data.propertySize || 0, value);
                // Clear lot size error when user starts typing
                if (lotSizeError && value > 0) {
                  setLotSizeError(null);
                  setShouldShakeLotSize(false);
                }
              }}
              onBlur={() => {
                // Validate lot size when field loses focus
                validateLotSize(data.lotSize);
              }}
              className={clsx(
                "w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                sizeError ? "border-red-500" : lotSizeError ? "border-red-500" : "border-gray-300",
                shouldShakeLotSize ? "animate-shake" : ""
              )}
              placeholder="e.g., 20,000"
            />
            {lotSizeError && (
              <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{lotSizeError}</span>
              </div>
            )}
          </div>

          {/* Site Condition */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Condition of Site
              </label>
              <Tooltip content="Assess the overall condition of the site, including the pavement, landscaping, and signage. Look for any signs of damage, deterioration, or improper maintenance. Consider the site's functionality, accessibility, and aesthetic appeal. Examples:&#13;&#10;New: Well-maintained site with new or recently upgraded features, excellent landscaping, and clear signage.&#13;&#10;Good: Site in good condition with minor wear and tear, functional and visually appealing.&#13;&#10;Average: Site showing signs of age and wear, may require some maintenance or improvements.&#13;&#10;Fair: Site with significant damage, poor landscaping, or inadequate signage, detracting from the property's appeal and functionality.">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <div className="flex gap-2">
              {propertyConditions.map((condition) => (
                <button
                  key={condition}
                  onClick={() => {
                    onUpdate({
                      assets: {
                        ...data.assets,
                        Site_Improvements: {
                          condition,
                          size: data.lotSize || 0
                        }
                      }
                    });
                  }}
                  className={clsx(
                    "px-4 py-2 rounded text-sm font-medium flex-1",
                    data.assets?.Site_Improvements?.condition === condition
                      ? "bg-fc-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {condition}
                </button>
              ))}
            </div>
          </div>

          {/* UST Age */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                UST Age 
              </label>
              <Tooltip content="Select the age of the underground storage tanks. Condition will be automatically determined based on age.">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <select
              value={data.ustAge || ''}
              onChange={(e) => {
                const selectedAge = e.target.value;
                let condition = 'Average'; // Default condition
                
                // Map age to condition
                if (selectedAge === '0-5 Years') condition = 'New';
                else if (selectedAge === '6-10 Years') condition = 'Good';
                else if (selectedAge === '11-15 Years') condition = 'Average';
                else if (selectedAge === '15+ Years') condition = 'Fair';
                
                // Update both ustAge and ustCondition
                onUpdate({ 
                  ustAge: selectedAge, 
                  ustCondition: condition,
                  assets: {
                    ...data.assets,
                    UST: {
                      condition: condition,
                      size: data.tankCapacity || 20000
                    }
                  }
                });
              }}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors?.ustAge ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select UST Age</option>
              {ustAgeOptions.map((age) => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>

          {/* UST Condition */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                UST Condition
              </label>
              <Tooltip content="Assess the condition of the underground storage tanks based on their age, maintenance history, and compliance with regulations.">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <div className="flex gap-2">
              {propertyConditions.map((condition) => (
                <button
                  key={condition}
                  onClick={() => {
                    onUpdate({
                      ustCondition: condition,
                      assets: {
                        ...data.assets,
                        UST: {
                          condition,
                          size: data.tankCapacity || 0
                        }
                      }
                    });
                  }}
                  className={clsx(
                    "px-4 py-2 rounded text-sm font-medium flex-1",
                    (data.ustCondition === condition || data.assets?.UST?.condition === condition)
                      ? "bg-fc-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {condition}
                </button>
              ))}
            </div>
          </div>

          {/* Tank Capacity */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Total Underground Storage Tank Capacity (Gallons)
              </label>
              <Tooltip content="Total capacity of all underground storage tanks">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="text"
              value={formatNumber(data.tankCapacity)}
              onChange={(e) => {
                const value = parseTankCapacity(e.target.value);
                onUpdate({
                  tankCapacity: value,
                  assets: {
                    ...data.assets,
                    UST: {
                      condition: data.ustCondition || data.assets?.UST?.condition || 'Average',
                      size: value
                    }
                  }
                });
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 20,000"
            />
          </div>

          {/* Empty div to push Year Built to next row */}
          <div></div>

          {/* Year Built */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Year Built
              </label>
              <Tooltip content="The year the property was originally constructed">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            {/* Always show database suggestion if available */}
            {data.databaseSuggestions?.yearBuilt && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-700">
                      💡 Our records show: <strong>{data.databaseSuggestions.yearBuilt}</strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate({ yearBuilt: data.databaseSuggestions?.yearBuilt })}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Use This Value
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1.5 leading-snug">Sourced from public records or prior entries. Use as a starting point only.</p>
              </div>
            )}
            <input
              type="text"
              value={data.yearBuilt ? formatYear(data.yearBuilt) : ''}
              onChange={(e) => {
                const year = parseNumber(e.target.value);
                onUpdate({ yearBuilt: year });
                validateYears(year, data.yearRenovated);
              }}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                yearError ? 'border-red-500' : errors?.yearBuilt ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g. 1985"
            />
          </div>

          {/* Empty div to push Year Renovated to next row */}
          <div></div>

          {/* Year Renovated */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Year Renovated
              </label>
              <Tooltip content="The year of the most recent significant renovation (if applicable)">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="text"
              value={formatYear(data.yearRenovated)}
              onChange={(e) => {
                const year = parseNumber(e.target.value);
                onUpdate({ yearRenovated: year });
                validateYears(data.yearBuilt, year);
              }}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                yearError ? 'border-red-500' : errors?.yearRenovated ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g. 2010"
            />
          </div>
        </div>

        {sizeError && (
          <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>{sizeError}</span>
          </div>
        )}

        {yearError && (
          <div className="text-red-500 text-sm mt-1">
            {yearError}
          </div>
        )}
      </div>
    </div>
  );
}